// src/portal-app/team/EmployeeScheduleTab.js
//
// W4 Task 3 — Schedule section tab within EmployeeDetailScreen.
// Receives `employeeId` (number) and `currency` (string) as props.
//
// Zones:
//   HoursBar         — total hours this week vs overtime threshold
//                      (getTenantSetting('payroll.overtime_threshold_hours_week', 40))
//   StationPrefs     — primary/secondary station dropdowns + save button
//   UpcomingShifts   — next 5 shifts (date >= today), GlassCard items
//   RecentShifts     — last 5 completed shifts with actual hours
//
// Services used (unchanged):
//   shiftService.getShiftsByDateRange(start, end)  (filtered client-side for empId)
//   shiftService.getEmployeeHours(empId, start, end)
//   stationPreferenceService.get(empId)
//   stationPreferenceService.set(empId, data)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { shiftService, stationPreferenceService } from '../../services/api';
import { StatusBadge } from '../operations/kit';
import { SurfaceStates } from '../insights/kit';
import { GlassCard } from '../shell/LiquidGlass';

/* ------------------------------------------------------------------ */
/* Stylesheet                                                           */
/* ------------------------------------------------------------------ */
const CSS = `
/* ---- schedule tab layout ---- */
.est-wrap{width:100%;box-sizing:border-box;padding:0 0 40px;}

/* ---- section header ---- */
.est-section-hdr{display:flex;align-items:center;justify-content:space-between;
  margin:16px 0 8px;}
.est-section-title{font-size:11px;font-weight:700;text-transform:uppercase;
  letter-spacing:.8px;color:var(--c-mutedLight);}

/* ---- card ---- */
.est-card{padding:16px;margin-bottom:10px;}
.est-card-title{font-size:13px;font-weight:700;text-transform:uppercase;
  letter-spacing:.8px;color:var(--c-textLight);margin-bottom:12px;}

/* ---- hours bar ---- */
.est-hours-header{display:flex;align-items:baseline;justify-content:space-between;
  margin-bottom:10px;}
.est-hours-label{font-size:13px;font-weight:700;color:var(--c-ink);}
.est-hours-value{font-size:22px;font-weight:800;color:var(--c-ink);
  font-variant-numeric:tabular-nums;line-height:1;}
.est-hours-value sup{font-size:13px;font-weight:600;color:var(--c-textLight);}
.est-hours-threshold{font-size:12px;font-weight:500;color:var(--c-textLight);}
.est-bar-track{height:8px;border-radius:999px;overflow:hidden;
  background:color-mix(in srgb,var(--c-ink) 8%,transparent);}
.est-bar-fill{height:100%;border-radius:999px;
  transition:width .4s ease;
  background:var(--c-green);}
.est-bar-fill[data-tone="amber"]{background:var(--c-amber);}
.est-bar-fill[data-tone="red"]{background:var(--c-red);}

/* ---- station prefs ---- */
.est-station-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;
  margin-bottom:14px;}
.est-field-lbl{display:block;font-size:11px;font-weight:700;
  text-transform:uppercase;letter-spacing:.8px;color:var(--c-textLight);
  margin-bottom:4px;}
.est-select{display:block;width:100%;box-sizing:border-box;
  min-height:44px;padding:10px 14px;border-radius:14px;
  border:1.5px solid color-mix(in srgb,var(--c-ink) 12%,transparent);
  background:color-mix(in srgb,var(--c-surface) 60%,transparent);
  font-family:inherit;font-size:14px;font-weight:500;color:var(--c-ink);
  appearance:none;cursor:pointer;outline:none;}
.est-select:focus{border-color:var(--c-green);}
.est-save-prefs-btn{width:100%;min-height:44px;border:none;border-radius:16px;
  background:var(--c-green);
  font-family:inherit;font-size:15px;font-weight:700;color:var(--c-white);
  cursor:pointer;display:inline-flex;align-items:center;justify-content:center;gap:8px;
  transition:transform .15s ease,opacity .15s ease;}
.est-save-prefs-btn:active{transform:scale(.97);}
.est-save-prefs-btn:disabled{opacity:.5;cursor:not-allowed;transform:none;}
.est-prefs-saved{font-size:12px;font-weight:600;color:var(--c-success);
  text-align:center;margin-top:6px;}
.est-prefs-error{font-size:12px;font-weight:600;color:var(--c-red);
  text-align:center;margin-top:6px;}
.est-spinner{width:16px;height:16px;border-radius:50%;flex:none;
  border:2.5px solid color-mix(in srgb,var(--c-white) 30%,transparent);
  border-top-color:var(--c-white);
  animation:estSpin .75s linear infinite;}
@keyframes estSpin{to{transform:rotate(360deg);}}

/* ---- shift item ---- */
.est-shift-item{display:flex;align-items:center;gap:10px;
  padding:10px 0;border-bottom:1px solid
  color-mix(in srgb,var(--c-ink) 6%,transparent);}
.est-shift-item:last-child{border-bottom:none;padding-bottom:0;}
.est-shift-accent{width:4px;border-radius:2px;flex:none;align-self:stretch;
  min-height:32px;}
.est-shift-body{flex:1;min-width:0;}
.est-shift-meta{display:flex;align-items:center;gap:8px;flex-wrap:wrap;}
.est-shift-date{font-size:13px;font-weight:700;color:var(--c-ink);}
.est-shift-time{font-size:12px;font-weight:500;color:var(--c-textLight);
  font-variant-numeric:tabular-nums;}
.est-shift-hours{font-size:11px;font-weight:700;color:var(--c-textLight);
  font-variant-numeric:tabular-nums;margin-left:auto;}
.est-shift-station{font-size:10px;font-weight:700;text-transform:uppercase;
  letter-spacing:.5px;padding:2px 7px;border-radius:999px;white-space:nowrap;}

/* ---- empty ---- */
.est-empty{padding:14px 0;font-size:13px;color:var(--c-mutedLight);
  text-align:center;}

@media(prefers-reduced-motion:reduce){
  .est-bar-fill{transition:none;}
  .est-spinner{animation-duration:1.5s;}
}
`;

let cssInjected = false;
function ensureCss() {
    if (cssInjected || typeof document === 'undefined') return;
    const tag = document.createElement('style');
    tag.id = 'est-css';
    tag.textContent = CSS;
    document.head.appendChild(tag);
    cssInjected = true;
}

/* ------------------------------------------------------------------ */
/* Station colour map                                                   */
/* ------------------------------------------------------------------ */
const STATION_COLORS = {
    Kitchen: { color: 'var(--c-amber)', bg: 'color-mix(in srgb,var(--c-amber) 12%,transparent)' },
    Bar: { color: 'var(--c-blue)', bg: 'color-mix(in srgb,var(--c-blue) 12%,transparent)' },
    Cashier: { color: 'var(--c-green)', bg: 'color-mix(in srgb,var(--c-green) 12%,transparent)' },
    FOH: { color: 'var(--c-purple)', bg: 'color-mix(in srgb,var(--c-purple) 12%,transparent)' },
};

function stationStyle(station) {
    return STATION_COLORS[station] || { color: 'var(--c-teal)', bg: 'color-mix(in srgb,var(--c-teal) 12%,transparent)' };
}

/* ------------------------------------------------------------------ */
/* Date helpers — local time only, no toISOString()                    */
/* ------------------------------------------------------------------ */
function localToday() {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function weekBounds() {
    const now = new Date();
    const dow = now.getDay(); // 0=Sun ... 6=Sat
    // Week starts Monday (ISO)
    const diffToMon = (dow === 0 ? -6 : 1 - dow);
    const mon = new Date(now);
    mon.setDate(now.getDate() + diffToMon);
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);

    const fmt = (d) => {
        const y = d.getFullYear();
        const mo = String(d.getMonth() + 1).padStart(2, '0');
        const da = String(d.getDate()).padStart(2, '0');
        return `${y}-${mo}-${da}`;
    };
    return { start: fmt(mon), end: fmt(sun) };
}

/* ------------------------------------------------------------------ */
/* HoursBar                                                             */
/* ------------------------------------------------------------------ */
function HoursBar({ totalHours, threshold, t }) {
    const pct = threshold > 0 ? (totalHours / threshold) * 100 : 0;
    const clampedPct = Math.min(pct, 100);

    let tone = 'green';
    if (pct >= 100) tone = 'red';
    else if (pct >= 80) tone = 'amber';

    return (
        <GlassCard veil className="est-card" data-testid="hours-progress-bar">
            <div className="est-hours-header">
                <div>
                    <div className="est-card-title">{t('hours_this_week', {}, 'Hours This Week')}</div>
                    <div className="est-hours-value">
                        {totalHours}
                        <sup>{t('hrs_label', {}, 'h')}</sup>
                    </div>
                </div>
                <div className="est-hours-threshold">
                    {t('of_threshold', {}, 'of')} {threshold}{t('hrs_label', {}, 'h')}
                </div>
            </div>
            <div className="est-bar-track">
                <div
                    className="est-bar-fill"
                    data-testid="hours-progress-fill"
                    data-tone={tone}
                    style={{ width: `${clampedPct}%` }}
                    role="progressbar"
                    aria-valuenow={totalHours}
                    aria-valuemin={0}
                    aria-valuemax={threshold}
                    aria-label={t('hours_this_week', {}, 'Hours this week')}
                />
            </div>
        </GlassCard>
    );
}

/* ------------------------------------------------------------------ */
/* StationPreferences                                                    */
/* ------------------------------------------------------------------ */
function StationPreferences({ employeeId, initialPrefs, stations, t }) {
    const [primary, setPrimary] = useState(initialPrefs.primary_station || '');
    const [secondary, setSecondary] = useState(initialPrefs.secondary_station || '');
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState('');

    const handleSave = async () => {
        setSaving(true);
        setSaved(false);
        setError('');
        try {
            await stationPreferenceService.set(employeeId, {
                primary_station: primary || null,
                secondary_station: secondary || null,
            });
            setSaving(false);
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch (err) {
            setSaving(false);
            setError(err?.response?.data?.detail || err?.message || t('save_failed', {}, 'Save failed'));
        }
    };

    return (
        <GlassCard veil className="est-card" data-testid="station-preferences">
            <div className="est-card-title">{t('station_preferences', {}, 'Station Preferences')}</div>
            <div className="est-station-grid">
                <div>
                    <label className="est-field-lbl">{t('primary_station', {}, 'Primary')}</label>
                    <select
                        className="est-select"
                        data-testid="primary-station"
                        value={primary}
                        onChange={(e) => { setPrimary(e.target.value); setSaved(false); }}
                    >
                        <option value="">{t('select_station', {}, '— Select —')}</option>
                        {stations.map((s) => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="est-field-lbl">{t('secondary_station', {}, 'Secondary')}</label>
                    <select
                        className="est-select"
                        data-testid="secondary-station"
                        value={secondary}
                        onChange={(e) => { setSecondary(e.target.value); setSaved(false); }}
                    >
                        <option value="">{t('select_station', {}, '— Select —')}</option>
                        {stations.map((s) => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                </div>
            </div>
            <button
                type="button"
                className="est-save-prefs-btn"
                data-testid="save-station-prefs-btn"
                disabled={saving}
                onClick={handleSave}
            >
                {saving && <span className="est-spinner" aria-hidden="true" />}
                {saving ? t('saving', {}, 'Saving…') : t('save', {}, 'Save')}
            </button>
            {saved && <div className="est-prefs-saved">{t('saved', {}, 'Saved!')}</div>}
            {error && <div className="est-prefs-error">{error}</div>}
        </GlassCard>
    );
}

/* ------------------------------------------------------------------ */
/* ShiftItem                                                             */
/* ------------------------------------------------------------------ */
function ShiftItem({ shift, showActual, t }) {
    const sc = stationStyle(shift.station);
    return (
        <div className="est-shift-item">
            <div className="est-shift-accent" style={{ background: sc.color }} />
            <div className="est-shift-body">
                <div className="est-shift-meta">
                    <span className="est-shift-date">{shift.date}</span>
                    <span
                        className="est-shift-station"
                        style={{ background: sc.bg, color: sc.color }}
                    >
                        {shift.station}
                    </span>
                </div>
                <div className="est-shift-meta" style={{ marginTop: 3 }}>
                    <span className="est-shift-time">
                        {shift.start_time} – {shift.end_time}
                    </span>
                    {showActual && shift.actual_hours != null
                        ? (
                            <span className="est-shift-hours">
                                {shift.actual_hours}{t('hrs_label', {}, 'h')}
                                {' '}{t('actual_label', {}, 'actual')}
                            </span>
                        )
                        : shift.expected_hours != null
                        ? (
                            <span className="est-shift-hours">
                                {shift.expected_hours}{t('hrs_label', {}, 'h')}
                            </span>
                        )
                        : null}
                </div>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* EmployeeScheduleTab                                                   */
/* ------------------------------------------------------------------ */
export default function EmployeeScheduleTab({ employeeId, currency }) {
    ensureCss();
    const { t, isRTL } = useLanguage();
    const { getTenantSetting, tenantConfig } = useAuth();

    const threshold = getTenantSetting('payroll.overtime_threshold_hours_week', 40);
    const stations = (tenantConfig && tenantConfig.stations) || ['Kitchen', 'Bar', 'Cashier', 'FOH'];

    const [status, setStatus] = useState('loading');
    const [totalHours, setTotalHours] = useState(0);
    const [shifts, setShifts] = useState([]);
    const [prefs, setPrefs] = useState({ primary_station: '', secondary_station: '' });

    const fetchRef = useRef(0);

    const loadAll = useCallback(async () => {
        const id = ++fetchRef.current;
        setStatus('loading');
        try {
            const { start, end } = weekBounds();
            // Fetch a wider range: past 4 weeks + next 2 weeks for recent + upcoming
            const now = new Date();
            const past4 = new Date(now);
            past4.setDate(now.getDate() - 28);
            const future2 = new Date(now);
            future2.setDate(now.getDate() + 14);

            const fmtD = (d) => {
                const y = d.getFullYear();
                const mo = String(d.getMonth() + 1).padStart(2, '0');
                const da = String(d.getDate()).padStart(2, '0');
                return `${y}-${mo}-${da}`;
            };

            const [shiftsData, hoursData, prefsData] = await Promise.all([
                shiftService.getShiftsByDateRange(fmtD(past4), fmtD(future2)),
                shiftService.getEmployeeHours(employeeId, start, end),
                stationPreferenceService.get(employeeId),
            ]);

            if (id !== fetchRef.current) return;
            setShifts(Array.isArray(shiftsData) ? shiftsData : []);
            setTotalHours(hoursData?.total_hours ?? 0);
            setPrefs(prefsData || {});
            setStatus('ready');
        } catch {
            if (id !== fetchRef.current) return;
            setStatus('error');
        }
    }, [employeeId]);

    useEffect(() => { loadAll(); }, [loadAll]);

    // Filter shifts to this employee only
    const myShifts = shifts.filter((s) => s.employee_id === employeeId);

    // Upcoming: date >= today, sorted ascending, take 5
    const today = localToday();
    const upcomingShifts = myShifts
        .filter((s) => s.date >= today)
        .sort((a, b) => a.date.localeCompare(b.date))
        .slice(0, 5);

    // Recent: completed, date < today, sorted descending, take 5
    const recentShifts = myShifts
        .filter((s) => s.status === 'Completed' && s.date < today)
        .sort((a, b) => b.date.localeCompare(a.date))
        .slice(0, 5);

    return (
        <div className="est-wrap" dir={isRTL ? 'rtl' : 'ltr'} data-testid="employee-schedule-tab">
            <SurfaceStates status={status} onRetry={loadAll}>
                {status === 'ready' && (
                    <>
                        {/* Hours this week */}
                        <HoursBar totalHours={totalHours} threshold={threshold} t={t} />

                        {/* Station preferences */}
                        <div className="est-section-hdr">
                            <span className="est-section-title">{t('station_preferences', {}, 'Station Preferences')}</span>
                        </div>
                        <StationPreferences
                            employeeId={employeeId}
                            initialPrefs={prefs}
                            stations={stations}
                            t={t}
                        />

                        {/* Upcoming shifts */}
                        <div className="est-section-hdr">
                            <span className="est-section-title">{t('upcoming_shifts', {}, 'Upcoming Shifts')}</span>
                        </div>
                        <GlassCard
                            veil
                            className="est-card"
                            data-testid="upcoming-shifts"
                        >
                            {upcomingShifts.length === 0
                                ? <div className="est-empty">{t('no_upcoming_shifts', {}, 'No upcoming shifts')}</div>
                                : upcomingShifts.map((s) => (
                                    <ShiftItem key={s.shift_id} shift={s} showActual={false} t={t} />
                                ))}
                        </GlassCard>

                        {/* Recent shifts */}
                        <div className="est-section-hdr">
                            <span className="est-section-title">{t('recent_shifts', {}, 'Recent Shifts')}</span>
                        </div>
                        <GlassCard
                            veil
                            className="est-card"
                            data-testid="recent-shifts"
                        >
                            {recentShifts.length === 0
                                ? <div className="est-empty">{t('no_recent_shifts', {}, 'No recent shifts')}</div>
                                : recentShifts.map((s) => (
                                    <ShiftItem key={s.shift_id} shift={s} showActual t={t} />
                                ))}
                        </GlassCard>
                    </>
                )}
            </SurfaceStates>
        </div>
    );
}
